import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Modal, Dialog } from '@/components/ui/Modal';
import { PageContainer } from '@/components/ui/PageContainer';
import { ClientsService, type ClientDTO, type Paginated } from '@/services/ClientsService';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { Plus, Building2, Mail, Phone, Pencil, Trash2 } from 'lucide-react';

export function Clients() {
  const svc = useMemo(() => new ClientsService(), []);
  const notifySuccess = useNotificationStore(s => s.success);
  const notifyError = useNotificationStore(s => s.error);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [data, setData] = useState<Paginated<ClientDTO> | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ClientDTO>({ id: '', name: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  
  // Permission checks
  const canCreate = hasAnyPermission([PERMISSIONS.CREATE_CLIENTS, PERMISSIONS.MANAGE_CLIENTS]);
  const canUpdate = hasAnyPermission([PERMISSIONS.UPDATE_CLIENTS, PERMISSIONS.MANAGE_CLIENTS]);
  const canDelete = hasAnyPermission([PERMISSIONS.DELETE_CLIENTS, PERMISSIONS.MANAGE_CLIENTS]);

  // Generate client ID from company name
  const generateClientId = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .substring(0, 50);
  };

  // Validate email
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!form.name.trim()) {
      errors.name = 'Company name is required';
    }
    
    if (form.contact_email && !validateEmail(form.contact_email)) {
      errors.contact_email = 'Please enter a valid email address';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  async function load() {
    setLoading(true);
    try {
      const result = await svc.list(page, pageSize);
      setData(result);
    } catch (e: any) {
      notifyError(e?.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [page]);

  // Handle edit client from navigation state
  useEffect(() => {
    const state = location.state as { editClient?: ClientDTO } | null;
    if (state?.editClient) {
      openEdit(state.editClient);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  function openCreate() {
    setEditingId(null);
    setForm({ id: '', name: '' });
    setFormErrors({});
    setModalOpen(true);
  }

  function openEdit(c: ClientDTO) {
    setEditingId(c.id);
    setForm({ ...c });
    setFormErrors({});
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
    setForm({ id: '', name: '' });
    setFormErrors({});
  }

  // Handle form field changes with validation
  const handleFieldChange = (field: keyof ClientDTO, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(e => ({ ...e, [field]: '' }));
    }
    
    // Auto-generate ID from name
    if (field === 'name' && !editingId) {
      setForm(f => ({ ...f, id: generateClientId(value) }));
    }
    
    // Validate email on change
    if (field === 'contact_email' && value) {
      if (!validateEmail(value)) {
        setFormErrors(e => ({ ...e, contact_email: 'Please enter a valid email address' }));
      }
    }
  };

  async function submit() {
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    try {
      if (editingId) {
        await svc.update(editingId, { 
          name: form.name, 
          contact_email: form.contact_email, 
          contact_name: form.contact_name, 
          contact_phone: form.contact_phone, 
          notes: form.notes 
        });
        notifySuccess('Client updated successfully');
      } else {
        await svc.create(form);
        notifySuccess('Client created successfully');
      }
      closeModal();
      await load();
    } catch (e: any) {
      notifyError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    try {
      // Optimistic UI update
      setData(prev => prev ? { ...prev, items: prev.items.filter(i => i.id !== id), pagination: { ...prev.pagination, totalItems: Math.max(0, prev.pagination.totalItems - 1) } } : prev);
      await svc.delete(id);
      notifySuccess('Client deleted successfully');
      setDeleteConfirmId(null);
      // Refresh to ensure server state consistency
      await load();
    } catch (e: any) {
      notifyError(e?.message || 'Delete failed');
    }
  }

  const filteredItems = data?.items.filter(client => 
    searchTerm === '' || 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <PageContainer>
      <PageHeader 
        title="Clients" 
        description="Manage your clients and their events"
      >
        {canCreate && (
          <Button size="sm" onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
            New Client
          </Button>
        )}
      </PageHeader>

      {/* Search Bar */}
      <div className="mb-6">
        <Input
          placeholder="Search clients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftIcon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          }
        />
      </div>

      {/* Loading State */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : filteredItems.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No clients found' : 'No clients yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ? 'Try adjusting your search' : 'Get started by creating your first client'}
            </p>
            {!searchTerm && canCreate && (
              <Button onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
                New Client
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((client) => (
            <Card
              key={client.id}
              className="group relative cursor-pointer hover:shadow-lg transition-shadow duration-200"
              onClick={() => navigate(`/organization/clients/${client.id}`)}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-lg leading-tight truncate pr-8">
                  {client.name}
                </CardTitle>
                {client.contact_name && (
                  <p className="text-sm text-gray-600 mt-1 truncate">
                    {client.contact_name}
                  </p>
                )}
              </CardHeader>
              
              <CardContent className="pt-0 pb-4">
                {/* Contact Info */}
                {(client.contact_email || client.contact_phone) && (
                  <div className="flex flex-col gap-2 mb-4">
                    {client.contact_email && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate" title={client.contact_email}>
                          {client.contact_email}
                        </span>
                      </div>
                    )}
                    {client.contact_phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate" title={client.contact_phone}>
                          {client.contact_phone}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons - Bottom Right */}
                {(canUpdate || canDelete) && (
                  <div className="flex items-center justify-end gap-1 mt-4 pt-4 border-t border-gray-100">
                    {canUpdate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(client);
                        }}
                        className="text-gray-600 hover:text-primary-600 hover:bg-primary-50"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(client.id);
                        }}
                        className="text-gray-600 hover:text-error-600 hover:bg-error-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="mt-8 flex justify-center">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-sm">
              Page {page} of {data.pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(data.pagination.totalPages, p + 1))}
              disabled={page === data.pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Client Modal */}
      <Modal 
        isOpen={modalOpen} 
        onClose={closeModal} 
        title={editingId ? 'Edit Client' : 'New Client'}
        description={editingId ? 'Update client information' : 'Create a new client for your organization'}
      >
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Company Name *</label>
            <Input 
              value={form.name} 
              onChange={(e) => handleFieldChange('name', e.target.value)} 
              placeholder="e.g., ACME Corporation"
              error={formErrors.name}
              required
            />
            {formErrors.name && (
              <p className="text-xs text-error-600 mt-1">{formErrors.name}</p>
            )}
            {!editingId && form.name && (
              <p className="text-xs text-gray-500 mt-1">Client ID: {form.id || generateClientId(form.name)}</p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Contact Name</label>
              <Input 
                value={form.contact_name || ''} 
                onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} 
                placeholder="e.g., John Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact Email</label>
              <Input 
                type="email"
                value={form.contact_email || ''} 
                onChange={(e) => handleFieldChange('contact_email', e.target.value.replace(/\s/g, ''))} 
                placeholder="e.g., john@acme.com"
                error={formErrors.contact_email}
              />
              {formErrors.contact_email && (
                <p className="text-xs text-error-600 mt-1">{formErrors.contact_email}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Contact Phone</label>
            <PhoneInput 
              value={form.contact_phone || ''} 
              onChange={(value) => handleFieldChange('contact_phone', value)} 
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <Textarea 
              value={form.notes || ''} 
              onChange={(e) => handleFieldChange('notes', e.target.value)} 
              placeholder="Any additional information..."
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              isLoading={saving}
              disabled={!form.name.trim() || Object.keys(formErrors).some(k => formErrors[k])}
            >
              {editingId ? 'Save Changes' : 'Create Client'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        isOpen={!!deleteConfirmId} 
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => deleteConfirmId && remove(deleteConfirmId)}
        title="Delete Client" 
        description="Are you sure you want to delete this client? This will also delete all associated events and attendees. This action cannot be undone."
        confirmText="Delete" 
        confirmVariant="danger"
      />
    </PageContainer>
  );
}


